import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  HelpOutline as HelpIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Payment as PaymentIcon,
  EventAvailable as BookingIcon,
  CancelPresentation as CancelPresentationIcon,
  Tour as TourIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { helpCenterService, globalService } from '../../services/firebase';
import { Timestamp } from 'firebase/firestore';
import type { HelpCenterData, HelpCategory, SupportMethod, ImportantNote, FAQItem } from '../../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';

const ManageHelpCenterPage: React.FC = () => {
  const [helpData, setHelpData] = useState<HelpCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const { t } = useTranslation();

  // Modal states
  const [categoryDialog, setCategoryDialog] = useState<{
    open: boolean;
    category: HelpCategory | null;
    isEdit: boolean;
  }>({ open: false, category: null, isEdit: false });

  const [supportDialog, setSupportDialog] = useState<{
    open: boolean;
    method: SupportMethod | null;
    isEdit: boolean;
  }>({ open: false, method: null, isEdit: false });

  const [noteDialog, setNoteDialog] = useState<{
    open: boolean;
    note: ImportantNote | null;
    isEdit: boolean;
  }>({ open: false, note: null, isEdit: false });

  useEffect(() => {
    fetchHelpData();
  }, []);

  const fetchHelpData = async () => {
    try {
      setLoading(true);
      const data = await helpCenterService.getHelpCenterData();
      setHelpData(data);
    } catch (err) {
      setError('Yardım merkezi verileri yüklenirken hata oluştu');
      console.error('Error fetching help center data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (field: 'title' | 'description' | 'searchPlaceholder', value: string) => {
    if (!helpData) return;
    const updatedData = setTranslatedData({ ...helpData }, field, selectedLang, value);
    setHelpData(updatedData);
  };

  const handleTranslateBasicInfo = async () => {
    if (!helpData) return;
    setSaving(true);
    try {
      let updatedData = { ...helpData };
      const sourceLang = 'tr';
      const targetLangs = ['en', 'ar'];

      // Translate title
      const titleSource = getTranslatedData(updatedData.title, sourceLang);
      if (titleSource) {
        const translations = await globalService.translateTexts({ text: titleSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'title', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'title', 'ar', translations.ar);
      }
      
      // Translate description
      const descriptionSource = getTranslatedData(updatedData.description, sourceLang);
      if (descriptionSource) {
        const translations = await globalService.translateTexts({ text: descriptionSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'description', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'description', 'ar', translations.ar);
      }

      // Translate searchPlaceholder
      const placeholderSource = getTranslatedData(updatedData.searchPlaceholder, sourceLang);
      if (placeholderSource) {
        const translations = await globalService.translateTexts({ text: placeholderSource, sourceLang, targetLangs });
        updatedData = setTranslatedData(updatedData, 'searchPlaceholder', 'en', translations.en);
        updatedData = setTranslatedData(updatedData, 'searchPlaceholder', 'ar', translations.ar);
      }

      setHelpData(updatedData);
      setSuccessMessage('Temel bilgiler çevirisi tamamlandı.');
    } catch (err) {
      setError('Temel bilgiler çevrilirken bir hata oluştu.');
      console.error('Translation error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    if (!helpData) return;

    try {
      setSaving(true);
      const updatedData = {
        ...helpData,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setSuccessMessage('Temel bilgiler başarıyla güncellendi');
    } catch (err) {
      setError('Temel bilgiler güncellenirken hata oluştu');
      console.error('Error updating basic info:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategory = async (category: HelpCategory) => {
    if (!helpData) return;

    try {
      setSaving(true);
      let updatedCategories;

      if (categoryDialog.isEdit) {
        updatedCategories = helpData.helpCategories.map(c =>
          c.id === category.id ? category : c
        );
      } else {
        const newCategory = {
          ...category,
          id: Date.now().toString(),
          order: helpData.helpCategories.length
        };
        updatedCategories = [...helpData.helpCategories, newCategory];
      }

      const updatedData = {
        ...helpData,
        helpCategories: updatedCategories,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setCategoryDialog({ open: false, category: null, isEdit: false });
      setSuccessMessage('Kategori başarıyla kaydedildi');
    } catch (err) {
      setError('Kategori kaydedilirken hata oluştu');
      console.error('Error saving category:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!helpData || !window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) return;

    try {
      setSaving(true);
      const updatedCategories = helpData.helpCategories.filter(c => c.id !== categoryId);
      const updatedData = {
        ...helpData,
        helpCategories: updatedCategories,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setSuccessMessage('Kategori başarıyla silindi');
    } catch (err) {
      setError('Kategori silinirken hata oluştu');
      console.error('Error deleting category:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSupportMethod = async (method: SupportMethod) => {
    if (!helpData) return;

    try {
      setSaving(true);
      let updatedMethods;

      if (supportDialog.isEdit) {
        updatedMethods = helpData.supportMethods.map(m =>
          m.id === method.id ? method : m
        );
      } else {
        const newMethod = {
          ...method,
          id: Date.now().toString(),
          order: helpData.supportMethods.length
        };
        updatedMethods = [...helpData.supportMethods, newMethod];
      }

      const updatedData = {
        ...helpData,
        supportMethods: updatedMethods,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setSupportDialog({ open: false, method: null, isEdit: false });
      setSuccessMessage('Destek yöntemi başarıyla kaydedildi');
    } catch (err) {
      setError('Destek yöntemi kaydedilirken hata oluştu');
      console.error('Error saving support method:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSupportMethod = async (methodId: string) => {
    if (!helpData || !window.confirm('Bu destek yöntemini silmek istediğinizden emin misiniz?')) return;

    try {
      setSaving(true);
      const updatedMethods = helpData.supportMethods.filter(m => m.id !== methodId);
      const updatedData = {
        ...helpData,
        supportMethods: updatedMethods,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setSuccessMessage('Destek yöntemi başarıyla silindi');
    } catch (err) {
      setError('Destek yöntemi silinirken hata oluştu');
      console.error('Error deleting support method:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async (note: ImportantNote) => {
    if (!helpData) return;

    try {
      setSaving(true);
      let updatedNotes;

      if (noteDialog.isEdit) {
        updatedNotes = helpData.importantNotes.map(n =>
          n.id === note.id ? note : n
        );
      } else {
        const newNote = {
          ...note,
          id: Date.now().toString(),
          order: helpData.importantNotes.length
        };
        updatedNotes = [...helpData.importantNotes, newNote];
      }

      const updatedData = {
        ...helpData,
        importantNotes: updatedNotes,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setNoteDialog({ open: false, note: null, isEdit: false });
      setSuccessMessage('Önemli not başarıyla kaydedildi');
    } catch (err) {
      setError('Önemli not kaydedilirken hata oluştu');
      console.error('Error saving note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!helpData || !window.confirm('Bu notu silmek istediğinizden emin misiniz?')) return;

    try {
      setSaving(true);
      const updatedNotes = helpData.importantNotes.filter(n => n.id !== noteId);
      const updatedData = {
        ...helpData,
        importantNotes: updatedNotes,
        lastUpdated: Timestamp.now()
      };

      await helpCenterService.updateHelpCenterData(updatedData);
      setHelpData(updatedData as HelpCenterData);
      setSuccessMessage('Not başarıyla silindi');
    } catch (err) {
      setError('Not silinirken hata oluştu');
      console.error('Error deleting note:', err);
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      booking: <BookingIcon />,
      payment: <PaymentIcon />,
      cancel: <CancelPresentationIcon />,
      tour: <TourIcon />,
      phone: <PhoneIcon />,
      whatsapp: <WhatsAppIcon />,
      email: <EmailIcon />,
      chat: <ChatIcon />
    };
    return icons[iconName] || <HelpIcon />;
  };

  const getAlertIcon = (type: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      info: <InfoIcon />,
      warning: <WarningIcon />,
      success: <SuccessIcon />,
      error: <ErrorIcon />
    };
    return icons[type] || <InfoIcon />;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Yükleniyor...</Typography>
      </Container>
    );
  }

  if (!helpData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">Veri yüklenemedi</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <HelpIcon sx={{ mr: 1 }} />
          Yardım Merkezi Yönetimi
        </Typography>
        <ToggleButtonGroup
          value={selectedLang}
          exclusive
          onChange={(e, newLang) => newLang && setSelectedLang(newLang)}
          aria-label="language"
        >
          <ToggleButton value="tr" aria-label="turkish">TR</ToggleButton>
          <ToggleButton value="en" aria-label="english">EN</ToggleButton>
          <ToggleButton value="ar" aria-label="arabic">AR</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {loading ? <CircularProgress /> : (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Temel Bilgiler</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Yardım Merkezi Başlığı"
                  value={getTranslatedData(helpData?.title, selectedLang)}
                  onChange={(e) => handleBasicInfoChange('title', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Yardım Merkezi Açıklaması"
                  value={getTranslatedData(helpData?.description, selectedLang)}
                  onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
                <TextField
                  label="Arama Çubuğu Metni"
                  value={getTranslatedData(helpData?.searchPlaceholder, selectedLang)}
                  onChange={(e) => handleBasicInfoChange('searchPlaceholder', e.target.value)}
                  fullWidth
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button
                    startIcon={<TranslateIcon />}
                    onClick={handleTranslateBasicInfo}
                    disabled={saving}
                  >
                    Tümünü Çevir
                  </Button>
                  <Button onClick={handleSaveBasicInfo} variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={24} /> : 'Kaydet'}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Yardım Kategorileri</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCategoryDialog({ open: true, category: null, isEdit: false })}
                sx={{ mb: 2 }}
              >
                Yeni Kategori Ekle
              </Button>
              {helpData?.helpCategories.map(category => (
                <Accordion key={category.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{getTranslatedData(category.title, selectedLang)}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Soru</TableCell>
                            <TableCell>Cevap</TableCell>
                            <TableCell>Aktif</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {category.questions.map(q => (
                            <TableRow key={q.id}>
                              <TableCell>{getTranslatedData(q.question, selectedLang)}</TableCell>
                              <TableCell>{getTranslatedData(q.answer, selectedLang).substring(0, 50)}...</TableCell>
                              <TableCell>{q.isActive ? 'Evet' : 'Hayır'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <IconButton onClick={() => setCategoryDialog({ open: true, category: category, isEdit: true })}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteCategory(category.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Destek Yöntemleri</Typography>
            </AccordionSummary>
            <AccordionDetails>
               <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setSupportDialog({ open: true, method: null, isEdit: false })}
                sx={{ mb: 2 }}
              >
                Yeni Destek Yöntemi Ekle
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Yöntem</TableCell>
                      <TableCell>İletişim</TableCell>
                      <TableCell>Çalışma Saati</TableCell>
                      <TableCell>Açıklama</TableCell>
                      <TableCell>Aktif</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {helpData?.supportMethods.map(method => (
                      <TableRow key={method.id}>
                        <TableCell>{getTranslatedData(method.title, selectedLang)}</TableCell>
                        <TableCell>{method.contact}</TableCell>
                        <TableCell>{getTranslatedData(method.time, selectedLang)}</TableCell>
                        <TableCell>{getTranslatedData(method.description, selectedLang)}</TableCell>
                        <TableCell>{method.isActive ? 'Evet' : 'Hayır'}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => setSupportDialog({ open: true, method: method, isEdit: true })}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteSupportMethod(method.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Önemli Notlar</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setNoteDialog({ open: true, note: null, isEdit: false })}
                sx={{ mb: 2 }}
              >
                Yeni Not Ekle
              </Button>
              <TableContainer component={Paper}>
                 <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Başlık</TableCell>
                      <TableCell>İçerik</TableCell>
                      <TableCell>Tip</TableCell>
                      <TableCell>Aktif</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {helpData?.importantNotes.map(note => (
                      <TableRow key={note.id}>
                        <TableCell>{getTranslatedData(note.title, selectedLang)}</TableCell>
                        <TableCell>{getTranslatedData(note.content, selectedLang)}</TableCell>
                        <TableCell>{note.type}</TableCell>
                        <TableCell>{note.isActive ? 'Evet' : 'Hayır'}</TableCell>
                        <TableCell>
                           <IconButton onClick={() => setNoteDialog({ open: true, note: note, isEdit: true })}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteNote(note.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <CategoryDialog
            open={categoryDialog.open}
            category={categoryDialog.category}
            isEdit={categoryDialog.isEdit}
            onClose={() => setCategoryDialog({ ...categoryDialog, open: false })}
            onSave={handleSaveCategory}
            saving={saving}
            selectedLang={selectedLang}
          />

          <SupportMethodDialog
            open={supportDialog.open}
            method={supportDialog.method}
            isEdit={supportDialog.isEdit}
            onClose={() => setSupportDialog({ ...supportDialog, open: false })}
            onSave={handleSaveSupportMethod}
            saving={saving}
            selectedLang={selectedLang}
          />

          <NoteDialog
            open={noteDialog.open}
            note={noteDialog.note}
            isEdit={noteDialog.isEdit}
            onClose={() => setNoteDialog({ ...noteDialog, open: false })}
            onSave={handleSaveNote}
            saving={saving}
            selectedLang={selectedLang}
          />
        </>
      )}
    </Container>
  );
};

const CategoryDialog: React.FC<{
  open: boolean;
  category: HelpCategory | null;
  isEdit: boolean;
  onClose: () => void;
  onSave: (category: HelpCategory) => void;
  saving: boolean;
  selectedLang: string;
}> = ({ open, category, isEdit, onClose, onSave, saving, selectedLang }) => {
  const [currentCategory, setCurrentCategory] = useState<HelpCategory | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (open) {
      if (category) {
        setCurrentCategory(JSON.parse(JSON.stringify(category)));
      } else {
        setCurrentCategory({ id: '', title: { tr: '', en: '', ar: '' }, icon: 'HelpIcon', color: '#000000', order: 0, isActive: true, questions: [] });
      }
    }
  }, [category, open]);
  
  const handleInputChange = (field: keyof Omit<HelpCategory, 'title' | 'questions'>, value: any) => {
    setCurrentCategory(prev => prev ? { ...prev, [field]: value } : null);
  };
  
  const handleTranslatableInputChange = (field: 'title', value: string) => {
    if (!currentCategory) return;
    const updatedData = setTranslatedData({ ...currentCategory }, field, selectedLang, value);
    setCurrentCategory(updatedData as HelpCategory);
  };

  const handleQuestionChange = (qIndex: number, field: 'question' | 'answer', value: string) => {
    setCurrentCategory(prev => {
      if (!prev) return null;
      const newQuestions = prev.questions.map((q, index) => {
        if (index === qIndex) {
          return setTranslatedData({ ...q }, field, selectedLang, value) as FAQItem;
        }
        return q;
      });
      return { ...prev, questions: newQuestions };
    });
  };

  const addQuestion = () => {
    setCurrentCategory(prev => {
      if (!prev) return null;
      const newQuestion: FAQItem = {
        id: Date.now().toString(),
        question: { tr: '', en: '', ar: '' },
        answer: { tr: '', en: '', ar: '' },
        isActive: true,
        order: prev.questions.length
      };
      return { ...prev, questions: [...prev.questions, newQuestion] };
    });
  };
  
  const removeQuestion = (qIndex: number) => {
    setCurrentCategory(prev => {
      if (!prev) return null;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== qIndex) };
    });
  };

  const handleTranslate = async () => {
    if (!currentCategory) return;
    setIsTranslating(true);
    try {
        const sourceLang = 'tr';
        const targetLangs = ['en', 'ar'];
        let translatedCategory = { ...currentCategory };

        // Translate category title
        const titleSourceText = getTranslatedData(translatedCategory.title, sourceLang);
        if (titleSourceText) {
            const titleTranslations = await globalService.translateTexts({ text: titleSourceText, sourceLang, targetLangs });
            translatedCategory = setTranslatedData(translatedCategory, 'title', 'en', titleTranslations.en);
            translatedCategory = setTranslatedData(translatedCategory, 'title', 'ar', titleTranslations.ar);
        }

        // Translate questions and answers
        const translatedQuestions = await Promise.all(currentCategory.questions.map(async (q) => {
            let translatedQ = { ...q };
            const questionSourceText = getTranslatedData(q.question, sourceLang);
            const answerSourceText = getTranslatedData(q.answer, sourceLang);

            if (questionSourceText) {
                const questionTranslations = await globalService.translateTexts({ text: questionSourceText, sourceLang, targetLangs });
                translatedQ = setTranslatedData(translatedQ, 'question', 'en', questionTranslations.en);
                translatedQ = setTranslatedData(translatedQ, 'question', 'ar', questionTranslations.ar);
            }

            if (answerSourceText) {
                const answerTranslations = await globalService.translateTexts({ text: answerSourceText, sourceLang, targetLangs });
                translatedQ = setTranslatedData(translatedQ, 'answer', 'en', answerTranslations.en);
                translatedQ = setTranslatedData(translatedQ, 'answer', 'ar', answerTranslations.ar);
            }
            return translatedQ;
        }));

        setCurrentCategory({ ...translatedCategory, questions: translatedQuestions });
    } catch (err) {
        console.error("Translation failed", err);
    } finally {
        setIsTranslating(false);
    }
  };

  const handleSave = () => {
    if (currentCategory) {
      onSave(currentCategory);
    }
  };

  if (!open || !currentCategory) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Kategori Başlığı"
          fullWidth
          value={getTranslatedData(currentCategory.title, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('title', e.target.value)}
          margin="normal"
        />
        <TextField
          label="İkon"
          fullWidth
          value={currentCategory.icon}
          onChange={(e) => handleInputChange('icon', e.target.value)}
          margin="normal"
        />
        <TextField
          label="Renk"
          fullWidth
          type="color"
          value={currentCategory.color}
          onChange={(e) => handleInputChange('color', e.target.value)}
          margin="normal"
        />
        <FormControlLabel
          control={<Switch checked={currentCategory.isActive} onChange={(e) => handleInputChange('isActive', e.target.checked)} />}
          label="Aktif"
        />
        <Typography variant="h6" sx={{ mt: 2 }}>Sorular</Typography>
        {currentCategory.questions.map((q, qIndex) => (
          <Accordion key={q.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{getTranslatedData(q.question, selectedLang) || 'Yeni Soru'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="Soru"
                fullWidth
                value={getTranslatedData(q.question, selectedLang)}
                onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                margin="dense"
              />
              <TextField
                label="Cevap"
                fullWidth
                multiline
                rows={3}
                value={getTranslatedData(q.answer, selectedLang)}
                onChange={(e) => handleQuestionChange(qIndex, 'answer', e.target.value)}
                margin="dense"
              />
              <IconButton onClick={() => removeQuestion(qIndex)}><DeleteIcon /></IconButton>
            </AccordionDetails>
          </Accordion>
        ))}
        <Button onClick={addQuestion} startIcon={<AddIcon />}>Soru Ekle</Button>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button
            startIcon={<TranslateIcon />}
            onClick={handleTranslate}
            disabled={saving || isTranslating}
          >
            {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
          </Button>
          <Button onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || isTranslating}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const SupportMethodDialog: React.FC<{
  open: boolean;
  method: SupportMethod | null;
  isEdit: boolean;
  onClose: () => void;
  onSave: (method: SupportMethod) => void;
  saving: boolean;
  selectedLang: string;
}> = ({ open, method, isEdit, onClose, onSave, saving, selectedLang }) => {
  const [currentMethod, setCurrentMethod] = useState<SupportMethod | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (open) {
      if (method) {
        setCurrentMethod(JSON.parse(JSON.stringify(method)));
      } else {
        setCurrentMethod({
          id: '',
          title: { tr: '', en: '', ar: '' },
          icon: 'PhoneIcon',
          contact: '',
          time: { tr: '', en: '', ar: '' },
          color: '#000000',
          description: { tr: '', en: '', ar: '' },
          order: 0,
          isActive: true,
        });
      }
    }
  }, [method, open]);

  const handleInputChange = (field: keyof Omit<SupportMethod, 'title' | 'time' | 'description'>, value: any) => {
    setCurrentMethod(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleTranslatableInputChange = (field: 'title' | 'time' | 'description', value: string) => {
    if (!currentMethod) return;
    const updatedData = setTranslatedData({ ...currentMethod }, field, selectedLang, value);
    setCurrentMethod(updatedData as SupportMethod);
  };

  const handleTranslate = async () => {
    if (!currentMethod) return;
    setIsTranslating(true);
    try {
        let updatedData = { ...currentMethod };
        const sourceLang = 'tr';
        const targetLangs = ['en', 'ar'];

        // Translate title
        const titleSource = getTranslatedData(updatedData.title, sourceLang);
        if (titleSource) {
          const translations = await globalService.translateTexts({ text: titleSource, sourceLang, targetLangs });
          updatedData = setTranslatedData(updatedData, 'title', 'en', translations.en);
          updatedData = setTranslatedData(updatedData, 'title', 'ar', translations.ar);
        }

        // Translate time
        const timeSource = getTranslatedData(updatedData.time, sourceLang);
        if (timeSource) {
          const translations = await globalService.translateTexts({ text: timeSource, sourceLang, targetLangs });
          updatedData = setTranslatedData(updatedData, 'time', 'en', translations.en);
          updatedData = setTranslatedData(updatedData, 'time', 'ar', translations.ar);
        }

        // Translate description
        const descriptionSource = getTranslatedData(updatedData.description, sourceLang);
        if (descriptionSource) {
          const translations = await globalService.translateTexts({ text: descriptionSource, sourceLang, targetLangs });
          updatedData = setTranslatedData(updatedData, 'description', 'en', translations.en);
          updatedData = setTranslatedData(updatedData, 'description', 'ar', translations.ar);
        }

        setCurrentMethod(updatedData);
    } catch (err) {
        console.error("Translation failed", err);
    } finally {
        setIsTranslating(false);
    }
  };

  const handleSave = () => {
    if (currentMethod) {
      onSave(currentMethod);
    }
  };

  if (!open || !currentMethod) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? 'Destek Yöntemini Düzenle' : 'Yeni Destek Yöntemi Ekle'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Başlık"
          fullWidth
          value={getTranslatedData(currentMethod.title, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('title', e.target.value)}
          margin="normal"
        />
        <TextField
          label="Açıklama"
          fullWidth
          value={getTranslatedData(currentMethod.description, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('description', e.target.value)}
          margin="normal"
          multiline
        />
        <TextField
          label="Süre (örn: 7/24, Hafta içi 09:00-18:00)"
          fullWidth
          value={getTranslatedData(currentMethod.time, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('time', e.target.value)}
          margin="normal"
        />
        <TextField
          label="İletişim Bilgisi (Tel, E-mail, vb.)"
          fullWidth
          value={currentMethod.contact}
          onChange={(e) => handleInputChange('contact', e.target.value)}
          margin="normal"
        />
        <TextField
          label="İkon"
          fullWidth
          value={currentMethod.icon}
          onChange={(e) => handleInputChange('icon', e.target.value)}
          margin="normal"
        />
        <TextField
          label="Renk"
          fullWidth
          type="color"
          value={currentMethod.color}
          onChange={(e) => handleInputChange('color', e.target.value)}
          margin="normal"
        />
        <FormControlLabel
          control={<Switch checked={currentMethod.isActive} onChange={(e) => handleInputChange('isActive', e.target.checked)} />}
          label="Aktif"
        />
      </DialogContent>
      <DialogActions>
        <Button
            startIcon={<TranslateIcon />}
            onClick={handleTranslate}
            disabled={saving || isTranslating}
          >
            {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
          </Button>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || isTranslating}>
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NoteDialog: React.FC<{
  open: boolean;
  note: ImportantNote | null;
  isEdit: boolean;
  onClose: () => void;
  onSave: (note: ImportantNote) => void;
  saving: boolean;
  selectedLang: string;
}> = ({ open, note, isEdit, onClose, onSave, saving, selectedLang }) => {
  const [currentNote, setCurrentNote] = useState<ImportantNote | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (open) {
      if (note) {
        setCurrentNote(JSON.parse(JSON.stringify(note)));
      } else {
        setCurrentNote({ id: '', title: { tr: '', en: '', ar: '' }, content: { tr: '', en: '', ar: '' }, type: 'info', order: 0, isActive: true });
      }
    }
  }, [note, open]);

  const handleInputChange = (field: keyof Omit<ImportantNote, 'title' | 'content'>, value: any) => {
    setCurrentNote(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleTranslatableInputChange = (field: 'title' | 'content', value: string) => {
    if (!currentNote) return;
    const updatedData = setTranslatedData({ ...currentNote }, field, selectedLang, value);
    setCurrentNote(updatedData as ImportantNote);
  };

  const handleTranslate = async () => {
    if (!currentNote) return;
    setIsTranslating(true);
    try {
        let updatedData = { ...currentNote };
        const sourceLang = 'tr';
        const targetLangs = ['en', 'ar'];

        // Translate title
        const titleSource = getTranslatedData(updatedData.title, sourceLang);
        if (titleSource) {
          const translations = await globalService.translateTexts({ text: titleSource, sourceLang, targetLangs });
          updatedData = setTranslatedData(updatedData, 'title', 'en', translations.en);
          updatedData = setTranslatedData(updatedData, 'title', 'ar', translations.ar);
        }

        // Translate content
        const contentSource = getTranslatedData(updatedData.content, sourceLang);
        if (contentSource) {
          const translations = await globalService.translateTexts({ text: contentSource, sourceLang, targetLangs });
          updatedData = setTranslatedData(updatedData, 'content', 'en', translations.en);
          updatedData = setTranslatedData(updatedData, 'content', 'ar', translations.ar);
        }
        
        setCurrentNote(updatedData);
    } catch (err) {
        console.error("Translation failed", err);
    } finally {
        setIsTranslating(false);
    }
  };

  const handleSave = () => {
    if (currentNote) {
      onSave(currentNote);
    }
  };

  if (!open || !currentNote) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Notu Düzenle' : 'Yeni Not Ekle'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Başlık"
          fullWidth
          value={getTranslatedData(currentNote.title, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('title', e.target.value)}
          margin="normal"
        />
        <TextField
          label="İçerik"
          fullWidth
          multiline
          rows={4}
          value={getTranslatedData(currentNote.content, selectedLang)}
          onChange={(e) => handleTranslatableInputChange('content', e.target.value)}
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Tip</InputLabel>
          <Select
            value={currentNote.type}
            label="Tip"
            onChange={(e) => handleInputChange('type', e.target.value as 'info' | 'warning' | 'success' | 'error')}
          >
            <MenuItem value="info">Bilgi</MenuItem>
            <MenuItem value="warning">Uyarı</MenuItem>
            <MenuItem value="success">Başarı</MenuItem>
            <MenuItem value="error">Hata</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={currentNote.isActive} onChange={(e) => handleInputChange('isActive', e.target.checked)} />}
          label="Aktif"
        />
      </DialogContent>
      <DialogActions>
        <Button
            startIcon={<TranslateIcon />}
            onClick={handleTranslate}
            disabled={saving || isTranslating}
          >
            {isTranslating ? 'Çevriliyor...' : 'Tümünü Çevir'}
          </Button>
        <Button onClick={onClose}>İptal</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || isTranslating}>
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageHelpCenterPage; 